import h from 'hyperscript';

import { getGlobalCssVariableValue } from '@disy/lib/dom';
import type { EventKey } from '@disy/lib/event-util';
import { on, unByKey } from '@disy/lib/event-util';
import { logger } from '@disy/lib/logging';
import { scrollIntoView } from '@disy/lib/scroll-into-view';
import type { Teardown } from '@disy/lib/teardown';
import { tearDown } from '@disy/lib/teardown';
import { closeAllPopups } from '@disy/ui/popup';

import { getStore } from 'cadenza/store/cadenza-store';
import { observeStickyElementStickiness } from 'cadenza/utils/sticky-utils';
import type { View } from 'cadenza/workbook/view/view';
import { EmptyView } from 'cadenza/workbook/view/view-empty/empty-view';
import type { ViewActionsToolbar } from 'cadenza/workbook/view/view-header/view-actions-toolbar';
import { isLinearLayoutForbidden } from 'cadenza/workbook/view/view-util';
import { getMaximizedViewId } from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import { compareViewByRowAndColumn } from 'cadenza/workbook/workbook-utils';
import { deactivateView, minimizeView, resetDetailView } from 'cadenza/workbook/workbook.state';
import { isLinearLayout, worksheetLayout } from 'cadenza/workbook/worksheet-layout';
import type { GridAreaDto } from 'cadenza/workbook/worksheet/dto/grid-area-dto';
import { setupWorksheetLayouting } from 'cadenza/workbook/worksheet/worksheet-grid/worksheet-layouting-setup';
import {
  addInertOnViewChildren,
  canBeEnlargedInLinearLayout,
  fromCssGridLayout,
  heightDependsOnGridRows,
  removeInertOnViewChildren,
  setGridRowDependentHeight,
  toCssGridLayout,
} from 'cadenza/workbook/worksheet/worksheet-grid/worksheet-layouting-util';

import './worksheet-grid.css';

/**
 * Coordinates that define the position of an DOM element inside a CSS grid container.
 * Format: "rowStart / colStart / rowEnd / colEnd"
 * You can use `convertWorksheetGridToCssGridArea()` and `convertCssGridAreaToWorksheetGrid()` to
 * convert between the worksheet grid format and the CSS grid format.
 *
 * @example
 * myElement.style['grid-area'] = '3 / 1 / 29 / 24';
 * @typedef {string} CssGridArea
 */

const HELPER_GRID_SIZE = Number(getGlobalCssVariableValue('--worksheet-helper-grid-size'));

const COMPONENT_NAME = 'd-worksheet-grid';
const WORKSHEET_GRID_GRID_CLASS = `${COMPONENT_NAME}--grid`;
export const VIEW_POPUP_CLASS = 'view-popup';

export class WorksheetGrid extends HTMLElement {
  readonly #grid;
  readonly #dropPreviewElem;
  readonly #swapPreviewElem;
  #gridLayoutingTeardowns: Teardown[] = [];
  readonly #linearLayoutTeardowns: Teardown[] = [];

  #eventKeys?: EventKey[];
  #viewsWhenEnablingMobileModeLastTime?: View[];

  constructor(views: View[]) {
    super();
    this.classList.add(COMPONENT_NAME);
    if (!isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-titlebar')) {
      this.classList.add(VIEW_POPUP_CLASS);
    }

    this.#grid = h(`.${WORKSHEET_GRID_GRID_CLASS}`);
    views.sort(compareViewByRowAndColumn).forEach((view) => this.#grid.append(view));

    // Visual helper grid: helps the user to align views consistently
    const numberOfCells = HELPER_GRID_SIZE * HELPER_GRID_SIZE;
    const visualHelperGridCells = Array(numberOfCells);
    for (let i = 0; i < numberOfCells; i++) {
      visualHelperGridCells[i] = h('.visual-helper-grid--cell');
    }

    // When the user moves a view, the drop preview element is shown to indicate where the view
    // would "land" would the user stop moving it now. It's constantly updated and shows the old
    // view position when the user tries to move the view to an invalid space.
    this.#dropPreviewElem = h('.drop-preview', { hidden: true });
    this.#grid.prepend(this.#dropPreviewElem);
    // When a view is about to be swapped, the swap preview shows the original position of the view
    // currently being dragged around.
    this.#swapPreviewElem = h('.swap-preview', { hidden: true });
    this.#grid.append(this.#swapPreviewElem);

    this.append(h('.visual-helper-grid', visualHelperGridCells), this.#grid);

    this.#setupWorksheetLayoutingIfApplicable();
  }

  connectedCallback() {
    // Wait for Views to build up.
    setTimeout(() => this.#applyChangesForMobile());
    this.#eventKeys = [
      on(worksheetLayout, 'change', () => {
        this.#applyChangesForMobile();
      }),
    ];
  }

  disconnectedCallback() {
    unByKey(this.#eventKeys);
  }

  onViewChanged() {
    this.#applyChangesForMobile();
  }

  #applyChangesForMobile() {
    if (isLinearLayoutForbidden()) {
      // Only apply mobile changes for "proper" workbooks, don't do anything
      // if it's e.g. a partial page that is only used to display a single view
      // or if a worksheet is about to get printed.
      return;
    }
    const mobileModeShouldBeApplied = isLinearLayout();
    if (mobileModeShouldBeApplied) {
      if (this.#viewsWhenEnablingMobileModeLastTime && isEqual(this.views, this.#viewsWhenEnablingMobileModeLastTime)) {
        // No changes to the last time mobileModeShouldBeApplied so do nothing
        return;
      }

      // For when a view changes its size, e.g. empty indicator view after assigning a measure.
      const resizeObserver = new ResizeObserver(() =>
        this.views.forEach((view) =>
          view.querySelector<ViewActionsToolbar>('.d-view-actions-toolbar')?.positionForLinearLayout(),
        ),
      );
      resizeObserver.observe(document.documentElement);

      this.#linearLayoutTeardowns.push(
        on(document, 'scroll', () => {
          closeAllPopups();
        }),
        resizeObserver,
      );

      const workbookHeader = document.querySelector<HTMLElement>('.d-workbook-header');
      if (workbookHeader) {
        this.#linearLayoutTeardowns.push(observeStickyElementStickiness(workbookHeader));
      }

      this.#gridLayoutingTeardowns = tearDown(this.#gridLayoutingTeardowns);
      getStore().dispatch(deactivateView());
      this.views.forEach((view) => {
        prepareViewForLinearLayout(view);
      });
      if (getMaximizedViewId(getStore().getState())) {
        getStore().dispatch(minimizeView());
      }
      this.#viewsWhenEnablingMobileModeLastTime = this.views;
      showDetailViewIfApplicable(this.views);
    } else if (this.#viewsWhenEnablingMobileModeLastTime) {
      tearDown(this.#linearLayoutTeardowns);
      resetDetailView();
      // No mobile mode should be applied atm
      // So if there was one applied the last time, we can reset everything
      this.#setupWorksheetLayoutingIfApplicable();
      this.views.forEach((view) => {
        removeLinearLayoutAdjustments(view);
      });
      filterOutViewsWithIntrinsicHeight(this.views).forEach((view) => (view.style.height = ''));
      this.#viewsWhenEnablingMobileModeLastTime = undefined;
    }
  }

  get views() {
    // CADENZA-23323: Don't check for the className here but for the tagName, because the
    // connectedCallback wasn't executed yet and so the className was not added yet
    return [...this.#grid.children].filter((elem) => elem.tagName === 'D-WORKSHEET-VIEW') as View[];
  }

  add(view: View | EmptyView) {
    // We insert the node here to the correct position to preserve a logical tab order and since
    // using tabindex is discouraged. Notice that a global repositioning of views in the dom would
    // end in too many views reloading their content than necessary.
    // See https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/tabindex
    const sortedViews = [...this.views, view].sort(compareViewByRowAndColumn);
    const viewAfter = sortedViews[sortedViews.indexOf(view) + 1];
    this.#grid.insertBefore(view, viewAfter);
    if (isLinearLayout() && !(view instanceof EmptyView)) {
      // Wait for the current task to build up the view.
      setTimeout(() => {
        getStore().dispatch(deactivateView());
        prepareViewForLinearLayout(view);
        scrollIntoView(view, { behavior: 'instant', block: 'start' });
        showDetailViewIfApplicable(this.views);
      });
    }
  }

  toggleDropPreviewVisibility(isVisible: boolean) {
    this.#dropPreviewElem.hidden = !isVisible;
  }

  /**
   * Positions the drop preview on the worksheet grid
   *
   * @param layout
   */
  setDropPreviewLayout(layout: GridAreaDto) {
    this.#dropPreviewElem.style.gridArea = convertWorksheetGridToCssGridArea(layout);
  }

  getDropPreviewLayout() {
    return convertCssGridAreaToWorksheetGrid(this.#dropPreviewElem.style.gridArea);
  }

  toggleSwapPreviewVisibility(isVisible: boolean) {
    this.#swapPreviewElem.hidden = !isVisible;
  }

  /**
   * Positions the view swap preview on the worksheet grid
   *
   * @param layout
   */
  setSwapPreviewLayout(layout: GridAreaDto) {
    this.#swapPreviewElem.style.gridArea = convertWorksheetGridToCssGridArea(layout);
  }

  #setupWorksheetLayoutingIfApplicable() {
    const layoutManagement = isWorkbookFeatureAvailable(getStore().getState(), 'workbook-layout-management');
    const mapViewSidebarResize = isWorkbookFeatureAvailable(getStore().getState(), 'workbook-map-view-sidebar-resize');
    if ((layoutManagement || mapViewSidebarResize) && !isLinearLayout()) {
      this.#gridLayoutingTeardowns = setupWorksheetLayouting(this, layoutManagement, mapViewSidebarResize);
    }
  }
}

customElements.define(COMPONENT_NAME, WorksheetGrid);

/**
 * Checks two arrays of workbook views to contain the same views/viewIds in the same order
 *
 * @param views1 - An array of workbook views
 * @param views2 - An array of workbook views
 * @return Returns true when both views arrays contain the same views/viewIds in the same order
 */
function isEqual(views1?: View[], views2?: View[]) {
  return (
    Array.isArray(views1) &&
    Array.isArray(views2) &&
    views1.length === views2.length &&
    views1.every((view, index) => view.viewId === views2[index].viewId)
  );
}

/**
 * Convert a CSS grid area to our worksheet grid format
 *
 * @param cssGridAreaProp
 * @return worksheet grid area
 */
function convertCssGridAreaToWorksheetGrid(cssGridAreaProp: string) {
  return fromCssGridLayout(cssGridAreaProp.split('/').map((str) => parseInt(str, 10)));
}

/**
 * Convert our worksheet grid format to CSS grid area format
 *
 * @param worksheetGridArea
 * @return CSS grid area
 */
function convertWorksheetGridToCssGridArea(worksheetGridArea: GridAreaDto) {
  return toCssGridLayout(worksheetGridArea).join('/');
}

/**
 * Sort out views that are shown with their intrinsic height when displayed on small devices.
 * @param views
 */
function filterOutViewsWithIntrinsicHeight(views: View[]) {
  return views.filter((view) => {
    return heightDependsOnGridRows(view);
  });
}

function prepareViewForLinearLayout(view: View) {
  setGridRowDependentHeight(view);
  view.classList.add('is-linear-layout');
  if (canBeEnlargedInLinearLayout(view)) {
    addInertOnViewChildren(view);
  }
}

function removeLinearLayoutAdjustments(view: View) {
  view.classList.remove('is-linear-layout');
  if (canBeEnlargedInLinearLayout(view)) {
    removeInertOnViewChildren(view);
    view.classList.remove('is-detail-view');
  } else {
    view.removeAttribute('inert');
  }
}

function showDetailViewIfApplicable(views: View[]) {
  if (views.length === 1) {
    const view = views[0];
    if (canBeEnlargedInLinearLayout(view)) {
      logger.warn('Activating the only view in a worksheet needs to be reimplemented.');
    }
  }
}
